import { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AppContext from '../../../context';
import Data from '../../../repository';
import Loading from '../../components/Loading';
import styles from '../../../styles/Services.module.scss';
import moment from 'moment';
import SumupWidget from '../../components/SumupWidget';
import {X} from 'react-feather';

function ServiceBook() {
	const { id } = useParams();
	const navigate = useNavigate();
	const context = useContext(AppContext);

	const [service, setService] = useState(null);
	const [staff, setStaff] = useState([]);
	const [selectedStaff, setSelectedStaff] = useState(null);
	const [availability, setAvailability] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [selectedTime, setSelectedTime] = useState(null);
	const [selectedSlots, setSelectedSlots] = useState([]);
	const [customerNotes, setCustomerNotes] = useState('');
	const [paymentMethod, setPaymentMethod] = useState('processor');
	const [loading, setLoading] = useState(true);
	const [loadingSlots, setLoadingSlots] = useState(false);
	const [booking, setBooking] = useState(false);
	const [error, setError] = useState(null);
	const [sumupCheckoutId, setSumupCheckoutId] = useState(null);
	const [sumupBookingId, setSumupBookingId] = useState(null);
	const [sumupError, setSumupError] = useState(null);
	const [waitlistKeys, setWaitlistKeys] = useState(new Set());
	const [waitlistLoading, setWaitlistLoading] = useState(null);
	const [waitlistMessage, setWaitlistMessage] = useState(null);
	const sumupPollRef = useRef(null);
	const isSumup = context.domain?.paymentProcessor === 'SumUp';
	const walletEnabled = !!context.domain?.walletEnabled;
	const walletBalance = Number(context.user?.walletBalance || 0);
	const cartTotal = selectedSlots.length * Number(service?.price || 0);
	const canUseWallet = walletEnabled && cartTotal > 0 && walletBalance >= cartTotal;

	// Generate next 14 days
	const dates = Array.from({ length: 14 }, (_, i) => {
		const date = moment().add(i, 'days');
		return {
			date: date.format('YYYY-MM-DD'),
			day: date.format('ddd'),
			dayNum: date.format('D'),
			month: date.format('MMM')
		};
	});

	useEffect(() => {
		fetchServiceAndStaff();
	}, [id]);

	useEffect(() => {
		if (selectedStaff && selectedDate) {
			fetchAvailability();
		}
	}, [selectedStaff, selectedDate]);

	const fetchServiceAndStaff = async () => {
		try {
			const [serviceRes, staffRes] = await Promise.all([
				Data.fetchService(id),
				Data.fetchServiceStaff(id)
			]);
			setService(serviceRes.data);
			setStaff(staffRes.data);
			const waitlistRes = await Data.fetchWaitlist();
			setWaitlistKeys(new Set(waitlistRes.data.filter(entry => entry.type === "variable").map(entry => entry.key)));

			// Auto-select first staff if only one
			if (staffRes.data.length === 1) {
				setSelectedStaff(staffRes.data[0]);
			}
		} catch (error) {
			console.error('Error fetching service:', error);
			setError('Service not found');
		} finally {
			setLoading(false);
		}
	};

	const fetchAvailability = async () => {
		setLoadingSlots(true);
		setSelectedTime(null);
		try {
			const start = selectedDate;
			const end = selectedDate;
			const res = await Data.fetchServiceAvailabilityWithUnavailable(id, start, end, selectedStaff._id);

			// Find slots for selected staff and date
			const staffData = res.data.find(s => s.staff._id === selectedStaff._id);
			if (staffData) {
				const dateData = staffData.dates.find(d => d.date === selectedDate);
				setAvailability(dateData?.slots || []);
			} else {
				setAvailability([]);
			}
		} catch (error) {
			console.error('Error fetching availability:', error);
			setAvailability([]);
		} finally {
			setLoadingSlots(false);
		}
	};

	const getSlotKey = (slot) => `${slot.staffId}-${slot.date}-${slot.startTime}`;
	const getWaitlistKey = (slot) => `variable:${id}:${slot.staffId}:${slot.date}:${slot.startTime}`;

	const toggleSlot = (slot) => {
		if (slot.available === false || slot.booked) return;
		setError(null);
		const key = getSlotKey(slot);
		setSelectedSlots((current) => {
			if (current.some(item => getSlotKey(item) === key)) {
				return current.filter(item => getSlotKey(item) !== key);
			}
			return [...current, slot];
		});
	};

	const toggleWaitlist = async (slot) => {
		const key = getWaitlistKey(slot);
		setWaitlistLoading(key);
		setError(null);
		setWaitlistMessage(null);

		const payload = {
			type: "variable",
			serviceId: id,
			staffId: slot.staffId,
			date: slot.date,
			startTime: slot.startTime
		};

		try {
			if (waitlistKeys.has(key)) {
				await Data.leaveWaitlist(payload);
				setWaitlistKeys(current => {
					const next = new Set(current);
					next.delete(key);
					return next;
				});
				setWaitlistMessage("You've left the waitlist.");
			} else {
				await Data.joinWaitlist(payload);
				setWaitlistKeys(current => new Set([...current, key]));
				setWaitlistMessage("You're on the waitlist. We'll email you if this time becomes available.");
			}
		} catch (err) {
			setError(err.response?.data?.message || "Couldn't update the waitlist.");
		} finally {
			setWaitlistLoading(null);
		}
	};

	const handleBook = async () => {
		if (!selectedStaff || !selectedSlots.length) return;

		setBooking(true);
		setError(null);
		setSumupError(null);

		try {
			const res = await Data.createBulkServiceBookings({
				bookings: selectedSlots.map(slot => ({
					serviceId: id,
					staffId: slot.staffId,
					date: slot.date,
					startTime: slot.startTime,
					customerNotes
				})),
				paymentMethod
			});

			if (paymentMethod === 'wallet') {
				await context.fetchUser();
				navigate(`/dashboard/my-service-bookings?success=true`);
				return;
			}

			if (isSumup) {
				const checkoutId = res.data?.checkoutId || res.data?.id || null;
				const bookingId = res.data?.bulkReference || checkoutId;
				if (!checkoutId || !bookingId) throw new Error("Missing SumUp checkout details");
				localStorage.setItem('pendingServiceBulkCheckoutId', checkoutId);
				setSumupCheckoutId(checkoutId);
				setSumupBookingId(bookingId);
			} else if (res.data.redirectUrl) {
				// Worldpay redirect
				window.location.href = res.data.redirectUrl;
			} else {
				// Free service - go to confirmation
				navigate(`/dashboard/my-service-bookings?success=true`);
			}
		} catch (error) {
			console.error('Error creating booking:', error);
			setError(error.response?.data?.message || 'Failed to create booking');
			setBooking(false);
		}
	};

	useEffect(() => {
		if (!sumupCheckoutId || !sumupBookingId) return;
		if (sumupPollRef.current) {
			clearInterval(sumupPollRef.current);
		}

		let cancelled = false;
		const poll = async () => {
			try {
				const result = await Data.verifySumupBulkServiceBookings(sumupBookingId);
				if (!cancelled && result.status === 200) {
					localStorage.removeItem('pendingServiceBulkCheckoutId');
					navigate(`/dashboard/my-service-bookings?success=true`);
				}
			} catch (err) {
				// Keep polling while payment is pending; widget callbacks handle errors.
			}
		};

		poll();
		sumupPollRef.current = setInterval(poll, 4000);

		return () => {
			cancelled = true;
			if (sumupPollRef.current) {
				clearInterval(sumupPollRef.current);
				sumupPollRef.current = null;
			}
		};
	}, [sumupCheckoutId, sumupBookingId, navigate]);

	const formatPrice = (price) => {
		if (price === 0) return 'Free';
		return `£${(price / 100).toFixed(2)}`;
	};

	const getInitials = (firstName, lastName) => {
		return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
	};

	if (loading) return <Loading text="Loading service..." />;
	if (error && !service) return <div className={styles.error}>{error}</div>;

	return (
		<div className={styles.serviceDetail}>
			<Link to="/dashboard/services" className={styles.backLink}>
				← Back to Services
			</Link>

			{/* Service Info */}
			<div className={styles.serviceInfo}>
				<h1>{service.name}</h1>
				{service.description && (
					<p className={styles.description}>{service.description}</p>
				)}
				<div className={styles.details}>
					<div className={styles.detail}>
						<span>Duration</span>
						<strong>{service.duration} mins</strong>
					</div>
					<div className={styles.detail}>
						<span>Price</span>
						<strong>{formatPrice(service.price)}</strong>
					</div>
				</div>
			</div>

			{/* Staff Selection */}
			{staff.length > 1 && (
				<div className={styles.staffSection}>
					<h2>Select Staff Member</h2>
					<div className={styles.staffList}>
						{staff.map(member => (
							<div
								key={member._id}
								className={`${styles.staffCard} ${selectedStaff?._id === member._id ? styles.selected : ''}`}
								onClick={() => setSelectedStaff(member)}
							>
								<div className={styles.avatar}>
									{member.image ? (
										<img src={member.image} alt={member.firstName} />
									) : (
										<div className={styles.initials}>
											{getInitials(member.firstName, member.lastName)}
										</div>
									)}
								</div>
								<div className={styles.info}>
									<h3>{member.firstName} {member.lastName}</h3>
									{member.title && <span className={styles.title}>{member.title}</span>}
								</div>
								<div className={`${styles.checkmark} ${selectedStaff?._id === member._id ? styles.checked : ''}`}>
									{selectedStaff?._id === member._id && '✓'}
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Show single staff info if only one */}
			{staff.length === 1 && (
				<div className={styles.staffSection}>
					<h2>Your Appointment With</h2>
					<div className={styles.staffCard} style={{ cursor: 'default', borderColor: 'var(--primary)' }}>
						<div className={styles.avatar}>
							{staff[0].image ? (
								<img src={staff[0].image} alt={staff[0].firstName} />
							) : (
								<div className={styles.initials}>
									{getInitials(staff[0].firstName, staff[0].lastName)}
								</div>
							)}
						</div>
						<div className={styles.info}>
							<h3>{staff[0].firstName} {staff[0].lastName}</h3>
							{staff[0].title && <span className={styles.title}>{staff[0].title}</span>}
						</div>
					</div>
				</div>
			)}

			{/* Date & Time Selection */}
			{selectedStaff && (
				<div className={styles.dateTimeSection}>
					<h2>Select Date & Time</h2>

					<div className={styles.dateSelector}>
						{dates.map(d => (
							<div
								key={d.date}
								className={`${styles.dateCard} ${selectedDate === d.date ? styles.selected : ''}`}
								onClick={() => setSelectedDate(d.date)}
							>
								<div className={styles.day}>{d.day}</div>
								<div className={styles.date}>{d.dayNum}</div>
								<div className={styles.month}>{d.month}</div>
							</div>
						))}
					</div>

					{selectedDate && (
						<>
							{loadingSlots ? (
								<Loading text="Loading available times..." />
							) : availability.length > 0 ? (
								<div className={styles.timeSlots}>
									{availability.map(slot => {
										const cartSlot = {
											staffId: selectedStaff._id,
											date: selectedDate,
											startTime: slot.startTime,
											available: slot.available,
											booked: slot.booked
										};
										const selected = selectedSlots.some(item => getSlotKey(item) === getSlotKey(cartSlot));
										const unavailable = slot.available === false || slot.booked;
										const waitlistKey = getWaitlistKey(cartSlot);
										const waitlisted = waitlistKeys.has(waitlistKey);
										return (
										<div
											key={slot.startTime}
											className={`${styles.timeSlot} ${selected ? styles.selected : ''} ${unavailable ? styles.unavailable : ''}`}
											onClick={() => {
												if (unavailable) return;
												setSelectedTime(slot.startTime);
												toggleSlot(cartSlot);
											}}
										>
											<span>{slot.startTime}</span>
											{selected && <span>In cart</span>}
											{unavailable && (
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														toggleWaitlist(cartSlot);
													}}
													disabled={waitlistLoading === waitlistKey}
												>
													{waitlistLoading === waitlistKey ? 'Updating...' : waitlisted ? 'Leave waitlist' : 'Join waitlist'}
												</button>
											)}
										</div>
									)})}
								</div>
							) : (
								<div className={styles.noSlots}>
									<p>No available times for this date.</p>
									<p>Please try another date.</p>
								</div>
							)}
						</>
					)}
				</div>
			)}

			{/* Notes */}
			{selectedSlots.length > 0 && (
				<div className={styles.notesSection}>
					<h2>Additional Notes (Optional)</h2>
					<textarea
						placeholder="Any special requests or information..."
						value={customerNotes}
						onChange={(e) => setCustomerNotes(e.target.value)}
					/>
				</div>
			)}

			{selectedSlots.length > 0 ? (
				<div className={styles.notesSection}>
					<h2>Cart</h2>
					<div className={styles.paymentMethodGroup}>
						{selectedSlots.map(slot => (
							<div key={getSlotKey(slot)} className={`${styles.paymentOption} ${styles.cartItem}`}>
								<span>{moment(slot.date).format('ddd, MMM D')} at {slot.startTime}</span>
								<button
									type="button"
									onClick={() => toggleSlot(slot)}
									aria-label={`Remove ${slot.startTime} from cart`}
								>
									<X size={18} />
								</button>
							</div>
						))}
					</div>
				</div>
			) : null}

			{selectedSlots.length > 0 && service?.price > 0 && walletEnabled ? (
				<div className={styles.notesSection}>
					<h2>Payment Method</h2>
					<div className={styles.paymentMethodGroup}>
						<label className={styles.paymentOption}>
							<input
								type="radio"
								name="paymentMethod"
								value="wallet"
								checked={paymentMethod === 'wallet'}
								onChange={() => setPaymentMethod('wallet')}
								disabled={!canUseWallet}
							/>
							<span>Use wallet credit (£{(walletBalance / 100).toFixed(2)} available)</span>
						</label>
						<label className={styles.paymentOption}>
							<input
								type="radio"
								name="paymentMethod"
								value="processor"
								checked={paymentMethod === 'processor'}
								onChange={() => setPaymentMethod('processor')}
							/>
							<span>Pay online</span>
						</label>
					</div>
					{!canUseWallet ? (
						<p className={styles.helperText}>You need {formatPrice(cartTotal)} in your wallet to book these appointments. <Link to="/dashboard/wallet">Top up your wallet</Link>.</p>
					) : null}
				</div>
			) : null}

	{/* Error Message */}
	{error && <p style={{ color: 'red', textAlign: 'center', marginBottom: '16px' }}>{error}</p>}
	{waitlistMessage && <p style={{ color: 'var(--primary)', textAlign: 'center', marginBottom: '16px' }}>{waitlistMessage}</p>}
	{sumupError && <p style={{ color: 'red', textAlign: 'center', marginBottom: '16px' }}>{sumupError}</p>}

	{sumupCheckoutId ? (
		<div style={{ marginBottom: '16px' }}>
			<SumupWidget
				checkoutId={sumupCheckoutId}
				onSuccess={async () => {
					try {
						setSumupError(null);
						const result = await Data.verifySumupBulkServiceBookings(sumupBookingId);
						if (result.status === 200) {
							localStorage.removeItem('pendingServiceBulkCheckoutId');
							navigate(`/dashboard/my-service-bookings?success=true`);
						}
					} catch (err) {
						setSumupError('There was an issue verifying your payment. Please refresh.');
					}
				}}
				onError={(message) => {
					setSumupError(message || 'Payment failed');
				}}
			/>
		</div>
	) : null}

	{/* Book Button */}
	<button
		className={styles.bookButton}
		disabled={!selectedStaff || !selectedSlots.length || booking || !!sumupCheckoutId}
		onClick={handleBook}
	>
		{booking ? 'Processing...' : sumupCheckoutId ? 'Payment Ready' : paymentMethod === 'wallet' ? 'Book using wallet' : `Book ${selectedSlots.length || ''}${selectedSlots.length ? ' for ' : ''}${formatPrice(cartTotal || service.price)}`}
	</button>
		</div>
	);
}

export default ServiceBook;
